import { FileSize, FileSizeUnit } from './../models/input-file.model';
export const mimeExtensionDictionary: Record<string, string> = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'image/svg+xml': '.svg',
  'application/pdf': '.pdf',
  'application/json': '.json',
  'text/plain': '.txt',
  'text/html': '.html',
  'text/css': '.css',
  'text/javascript': '.js',
  'application/javascript': '.js',
  'application/zip': '.zip',
  'audio/mpeg': '.mp3',
  'video/mp4': '.mp4'
};

/**
 * Read the File to create a base64 string
 * @param file : File - Fiel to be converted
 * @returns A promise with the base64
 */
/**
 * Opens a Blob in a new browser tab and optionally triggers a download.
 *
 * @param blob - The Blob object representing the file.
 * @param fileName - Optional file name for download (e.g. "report.pdf").
 */
export function openBlob(blob: Blob, fileName?: string): void {
  // Create a temporary object URL for the blob
  const url = URL.createObjectURL(blob);

  // Open in a new tab
  window.open(url, '_blank');

  // Optional: trigger download
  if (fileName) {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
  }

  // Clean up to free memory
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 5000);
}

export function convertBlobToText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsText(blob);
  });
}

export function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

/**
 * Adds or replaces the file extension of a given filename based on its MIME type.
 *
 * @param filename The base filename, with or without an existing extension.
 * @param mimeType The MIME type used to determine the correct file extension.
 * @returns The filename updated with the appropriate extension for the provided MIME type.
 * @throws If the MIME type does not have a mapped extension.
 */
export function addExtensionByMime(filename: string, mimeType: string): string {
  const ext = mimeExtensionDictionary[mimeType];
  if (!ext) {
    throw new Error(`No extension mapped for MIME type: ${mimeType}`);
  }

  return filename.replace(/\.[^/.]+$/, '') + ext;
}

export function parseFileSize(size: FileSize | undefined | null): {
  bytes: number;
  unit: FileSizeUnit;
} {
  if (!size) return { bytes: 0, unit: 'kb' };
  const value = size.slice(0, -2);
  const unit = size.slice(-2);
  const limits: Record<string, number> = {
    kb: 1000,
    mb: 1000 * 1000,
    gb: 1000 * 1000 * 1000
  };
  const bytes = Number(value) * (limits[unit] || 1);

  return { bytes, unit: unit as FileSizeUnit };
}

export function toFileSize(bytes: number): FileSize {
  const limits: Record<FileSizeUnit, number> = {
    kb: 1000 * 1000 - 1,
    mb: 1000 * 1000 * 1000 - 1,
    gb: 1000 * 1000 * 1000 * 1000 - 1
  };

  const divisions: Record<FileSizeUnit, number> = {
    kb: 1000,
    mb: 1000 * 1000,
    gb: 1000 * 1000 * 1000
  };

  const unit = Object.keys(limits).find(
    (key) => limits[key as FileSizeUnit] >= bytes
  ) as FileSizeUnit;
  if (!unit) return '0 kb';
  const value = bytes / (divisions[unit] || 1);
  return `${Number(value.toFixed(2))}${unit}`;
}
